import { Badge, Tooltip } from 'antd';
import React from 'react';
import { FaCheck, FaSuitcase, FaTimes } from 'react-icons/fa';
import { MdLuggage } from 'react-icons/md';
import './flightBaggage.less';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';

interface Props {
  isIncludeTrolly?: boolean | number;
  insIncludeSuitCase?: boolean | number;
  color?: string;
  /**
   * `compact` — two small icon chips (bag + ✓/✕) whose full sentence lives in
   * the tooltip and aria-label. Used by the flight leg row, where two lines of
   * "Suitcase included / Trolley included" text per leg was most of the card's
   * height. `default` keeps the original labelled badges for other callers.
   */
  variant?: 'default' | 'compact';
}

const FlightBaggage = ({
  isIncludeTrolly,
  insIncludeSuitCase,
  color,
  variant = 'default',
}: Props) => {
  const { searchDatePicker: t } = useSelector(
    (data: RootState) => data.config.translations
  );

  if (variant === 'compact') {
    const chips = [
      {
        key: 'suitcase',
        included: !!insIncludeSuitCase,
        Icon: FaSuitcase,
        label: insIncludeSuitCase
          ? t.suitcaseIsIncluded
          : t.suitcaseIsNotIncluded,
      },
      {
        key: 'trolly',
        included: !!isIncludeTrolly,
        Icon: MdLuggage,
        label: isIncludeTrolly ? t.trollyIsIncluded : t.trollyIsNotIncluded,
      },
    ];

    return (
      <div className="baggage baggage--compact">
        {chips.map(({ key, included, Icon, label }) => (
          <Tooltip
            key={key}
            title={label}
            // Tap opens it too — hover alone never fires on touch screens.
            trigger={['hover', 'click']}
          >
            <span
              className={`bag-chip ${included ? 'is-included' : 'is-excluded'}`}
              role="img"
              aria-label={label}
            >
              <Icon className="bag-chip-icon" />
              {included ? (
                <FaCheck className="bag-chip-status" />
              ) : (
                <FaTimes className="bag-chip-status" />
              )}
            </span>
          </Tooltip>
        ))}
      </div>
    );
  }

  return (
    <div className="baggage">
      <Badge
        size="small"
        color={insIncludeSuitCase ? '#8cca56' : '#f58087'}
        count={insIncludeSuitCase ? insIncludeSuitCase : undefined}
        dot
      >
        <FaSuitcase size={20} color={color ?? undefined} />
        <span
          style={{
            color: insIncludeSuitCase ? '#8cca56' : '#f58087',
          }}
        >
          {insIncludeSuitCase ? t.suitcaseIsIncluded : t.suitcaseIsNotIncluded}
        </span>
      </Badge>
      <Badge
        size="small"
        color={isIncludeTrolly ? '#8cca56' : '#f58087'}
        count={isIncludeTrolly ? isIncludeTrolly : undefined}
        dot
      >
        <MdLuggage size={20} color={color ?? undefined} />
        <span
          style={{
            color: isIncludeTrolly ? '#8cca56' : '#f58087',
          }}
        >
          {isIncludeTrolly ? t.trollyIsIncluded : t.trollyIsNotIncluded}
        </span>
      </Badge>
    </div>
  );
};

export default FlightBaggage;
